import { Pencil } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { CompareTable, DroveTheDifference, ScenarioBadge, ScenarioEditor, ScenarioSummary } from "@/components/scenarios/Scenarios";
import { useStore } from "@/model/store";
import type { Scenario } from "@/model/types";

/** One scenario: what it sets, what that changes versus Baseline, and why. */
export function ScenarioPage() {
	const { scenarioId } = useParams();
	// The id, or the slug (the API accepts either).
	const scenario = useStore((s) => s.world.scenarios.find((x) => x.id === scenarioId || x.slug === scenarioId));
	const updateScenario = useStore((s) => s.updateScenario);
	if (!scenario)
		return (
			<p className="p-10">
				This scenario doesn’t exist. It may have been deleted.{" "}
				<Link to="/" className="underline underline-offset-2">
					Back to Home
				</Link>
			</p>
		);
	return (
		<div className="max-w-5xl space-y-6">
			<header>
				<div className="text-xs text-muted-foreground">Scenario</div>
				<ScenarioName scenario={scenario} />
				<div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
					<ScenarioBadge scenario={scenario} />
					<ScenarioSummary scenario={scenario} />
				</div>
				<Textarea
					key={scenario.id + (scenario.description ?? "")}
					defaultValue={scenario.description ?? ""}
					placeholder="What this scenario assumes, in a sentence."
					className="mt-2 min-h-0 resize-none border-transparent bg-transparent px-1 font-serif text-lg shadow-none hover:border-border"
					rows={1}
					onBlur={(e) => e.target.value !== (scenario.description ?? "") && void updateScenario(scenario.id, { description: e.target.value })}
					aria-label="Scenario description"
				/>
			</header>
			<ScenarioEditor scenario={scenario} />
			<CompareTable scenario={scenario} />
			<DroveTheDifference scenario={scenario} />
		</div>
	);
}

/** The heading is the name: click it to rename (review 8.3). */
function ScenarioName({ scenario }: { scenario: Scenario }) {
	const updateScenario = useStore((s) => s.updateScenario);
	const [editing, setEditing] = useState(false);
	const heading = "text-3xl font-semibold tracking-tight";

	function save(value: string) {
		setEditing(false);
		const v = value.trim();
		if (v && v !== scenario.name) void updateScenario(scenario.id, { name: v });
	}

	if (editing)
		return (
			<input
				autoFocus
				defaultValue={scenario.name}
				aria-label="Scenario name"
				className={`${heading} -mx-1 w-full rounded-md border border-input bg-transparent px-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50`}
				onFocus={(e) => e.target.select()}
				onBlur={(e) => save(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") save(e.currentTarget.value);
					if (e.key === "Escape") setEditing(false);
				}}
			/>
		);
	return (
		<h1 className={heading}>
			<button
				type="button"
				onClick={() => setEditing(true)}
				title="Click to rename"
				className="group -mx-1 inline-flex items-center gap-2 rounded-md border border-transparent px-1 text-left outline-none hover:border-input focus-visible:ring-[3px] focus-visible:ring-ring/50"
			>
				{scenario.name}
				<Pencil className="size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
				<span className="sr-only">(rename)</span>
			</button>
		</h1>
	);
}
